import { fromJS, List as IList, Map as IMap } from 'immutable';
import { createSelector } from 'reselect';
import { Big } from 'big.js';

import { getAddress } from '../address/addressSelectors';
import {
    getMultipleCurrenciesSupported,
    getElectronicCurrencyList,
    getMaxTransactionAmount,
    getPlasticCurrencyList,
    getUYOPFeeAmount,
    getPlasticActivationFeeAmount,
    getDigitalActivationFeeAmount
} from '../catalogs/catalogSelectors';
import {
    faceplateForCode,
    hasDigital as hasDigitalFaceplates,
    hasPlastic as hasPlasticFaceplates
} from '../faceplate/faceplateSelectors';
import {
    getPromoRewardCards,
    hasPromoRewards,
    getPromoLabel,
    getDiscountRewardAmount,
    getShippingPromoData,
    getMatchingShippingPromoMethod,
    getAccessoriesPromoData
} from '../promo/promoSelectors';
import { getAccessoryCostByCurrency } from '../accessories/accessoriesSelectors';
import {
    getShippingMethod,
    getItemShippingMethodProfile,
    getShippingMethodProfileId
} from '../shipping/shippingSelectors';
import { getSupportedUYO } from '../uyo/uyoSelectors';

import {
    IS_PLASTIC_FIELD,
    IS_SELFBUY_FIELD,
    RECIPIENT_PLASTIC_NOTIFICATION,
    ITEM_ACCESSORY,
    RECIPIENT_NAME_FIELD,
    giftCardFieldNames,
    newItemIsPlastic,
    newItemDenomination
} from './newItemForm';
import {
    ITEM_DISPLAY_STORE_NAME,
    SHOW_DETAILS_KEY,
    EDIT_DETAILS_KEY,
    EDIT_ADDRESS_KEY,
    EDIT_QUANTITY_KEY,
    ITEM_FOCUS_SHIPPING_SELECT,
    SHIPPING_ADDRESS_CHANGED,
    SHIPPING_METHOD_CHANGED
} from './itemDisplayModule';



export const isPlasticItem = item => item.get(IS_PLASTIC_FIELD);


export const isPromoItem = item => item.has('category') && item.get('category') === 'PRMO';


export const isSelfBuyItem = item => item.get(IS_SELFBUY_FIELD);


export const hasPlasticNotification = item => item.get(RECIPIENT_PLASTIC_NOTIFICATION);


export const getItemRecipientName = item => item.get(RECIPIENT_NAME_FIELD, null);


export const getItemId = item => (
    isPromoItem(item) ? `${parseFloat(item.get('cardValue'))}-${item.getIn(['faceplate', 'code'])}` : item.get('id')
);


export const getItemQuantity = item => (
    item.get(giftCardFieldNames.QUANTITY_FIELD, 1)
);


export const getItemCurrency = item => (
    item.get(giftCardFieldNames.CURRENCY_FIELD, 'USD')
);


export const getItemDenomination = item => (
    isPromoItem(item) ? parseFloat(item.get('cardValue')) : parseFloat(item.get(giftCardFieldNames.AMOUNT_FIELD))
);


export const getIsNonDenomItem = item => (
    getItemDenomination(item) === 0.01
);


export const getPromoItemDenomination = item => (
    getIsNonDenomItem(item) ? parseInt(item.get('cardValue'), 10) : parseFloat(item.get('cardValue'))
);


export const getItemLabel = item => (
    isPromoItem(item) ? getPromoLabel(item) : ''
);


export const getItemFaceplate = (item, state) => {
    const faceplateData = isPromoItem(item) ? item.get('faceplate') : item.get('faceplateData');
    return faceplateData || faceplateForCode(state, item.get('faceplate'));
};


export const getUyoUrl = item => (
    item.get('uyoImageURL')
);


export const getCardsCount = items => (
    items.reduce((acc, item) => (acc + getItemQuantity(item)), 0)
);


export const sumFaceDenoms = items => (
    items.reduce((acc, item) => (
        new Big(acc).add(new Big(getItemDenomination(item)).times(getItemQuantity(item)))
    ), 0)
);


export const filterPaidItems = items => (
    items.filter(item => !isPromoItem(item))
);


export const filterPromoItems = items => (
    items.filter(item => isPromoItem(item))
);


export const getItemTotalCost = item => (
    new Big(getItemDenomination(item)).times(getItemQuantity(item)) || new Big('0')
);





export const getItems = state => state.get('items', IList());


export const getActiveItems = (state) => {
    const items = getItems(state);
    return items.filterNot(item => item.get('isRemoved') === true);
};


export const getDisplay = state => state.get('itemDisplay');


export const getShippingAddress = (state, item) => (
    item.has('addressID') ? getAddress(state, item.get('addressID')) : null
);


export const getShippingCost = (state, item) => {
    const method = getShippingMethod(
        state,
        getItemShippingMethodProfile(item),
        item.get('shippingMethod')
    );
    return parseFloat(method.get('cost'));
};


export const shippingCostCreator = state => (
    (item) => {
        const method = getShippingMethod(
            state,
            getItemShippingMethodProfile(item),
            item.get('shippingMethod')
        );
        return method ? parseFloat(method.get('cost')) : 0.00;
    }
);






export const itemHasAccessory = item => !!item.get(ITEM_ACCESSORY);


export const getItemAccessory = item => item.get(ITEM_ACCESSORY, IMap());


export const getItemsWithAccessories = createSelector(
    getActiveItems,
    items => items.filter(itemHasAccessory)
);


export const getItemWithUpdatedPromoAccessoryCosts = createSelector(
    [itemHasAccessory, getItemCurrency, getItemAccessory, item => item],
    (hasAccessory, currency, accessory, item) => (accessoriesPromoData) => {
        if (hasAccessory) {
            const accessoryCost = getAccessoryCostByCurrency(accessory, accessoriesPromoData, currency);
            return item.setIn(['accessory', 'costs', currency, 'cost'], accessoryCost);
        }
        return item;
    }
);


export const getItemAccessoryTotalCost = createSelector(
    [getItemAccessory, getItemCurrency, getItemQuantity],
    (accessory, currency, quantity) => accessoryPromoData => (
        new Big(getAccessoryCostByCurrency(accessory, accessoryPromoData, currency)).times(quantity)
    )
);


export const getItemAccessoriesGrandTotalCost = createSelector(
    getItemsWithAccessories,
    getAccessoriesPromoData,
    (items, accessoryPromoData) => items.reduce(
        (acc, item) => (acc.add(getItemAccessoryTotalCost(item)(accessoryPromoData))), new Big('0')
    )
);


export const getItemsAccessoryCount = createSelector(
    getItemsWithAccessories,
    items => items.reduce((acc, item) => acc + getItemQuantity(item), 0)
);


export const findItemById = (state, id) => (
    state.get('items').find(item => item.get('id') === id)
);


export const getPaidItems = createSelector(
    getActiveItems,
    filterPaidItems
);


export const getPaidItemsCount = createSelector(
    getPaidItems,
    items => items.size
);


export const getPaidCardsCount = createSelector(
    getPaidItems,
    items => getCardsCount(items)
);


export const getPromoItems = createSelector(
    getPromoRewardCards,
    (rewards) => {
        if (rewards.size <= 0) return IList();

        const promoItems = IMap().withMutations((itemsMap) => {
            
            
            rewards.forEach((reward) => {
                
                const rewardKey = getItemId(reward);

                if (!itemsMap.has(rewardKey)) {
                    
                    const missingProperties = {
                        id: rewardKey,
                        quantity: 1,
                        isEditable: false
                    };
                    itemsMap.set(rewardKey, reward.merge(missingProperties));
                } else {
                    itemsMap.updateIn([rewardKey, 'quantity'], quantity => quantity + 1);
                }
            });
            return itemsMap;
        });

        return promoItems.toList();
    }
);


export const getDigitalItems = createSelector(
    getActiveItems,
    items => items.filter(item => !isPlasticItem(item))
);


export const getPaidDigitalItems = createSelector(
    getDigitalItems,
    getAccessoriesPromoData,
    (digitalItems, promoData) => (
        filterPaidItems(digitalItems)
            .map(item => getItemWithUpdatedPromoAccessoryCosts(item)(promoData))
    )
);


export const getPaidDigitalCardsCount = createSelector(
    getPaidDigitalItems,
    items => getCardsCount(items)
);


export const getPlasticItems = createSelector(
    getActiveItems,
    items => items.filter(item => isPlasticItem(item))
);


export const getPaidPlasticItems = createSelector(
    getPlasticItems,
    filterPaidItems
);


export const getPaidPlasticCardsCount = createSelector(
    getPaidPlasticItems,
    items => getCardsCount(items)
);




export const hasDigitalItems = createSelector(
    getDigitalItems,
    items => items.size > 0
);


export const hasPlasticItems = createSelector(
    getPlasticItems,
    items => items.size > 0
);


export const hasPromoItems = state => hasPromoRewards(state);


export const getShowDetails = (state, context, item) => (
    state.getIn([ITEM_DISPLAY_STORE_NAME, item.get('id'), context, SHOW_DETAILS_KEY])
        || false
);


export const getEditDetails = (state, item) => (
    state.getIn([ITEM_DISPLAY_STORE_NAME, item.get('id'), EDIT_DETAILS_KEY], false)
);


export const getEditAddress = (state, item) => (
    state.getIn([ITEM_DISPLAY_STORE_NAME, item.get('id'), EDIT_ADDRESS_KEY], false)
);


export const getFocusedShipping = (state, item) => (
    state.getIn([ITEM_DISPLAY_STORE_NAME, item.get('id'), ITEM_FOCUS_SHIPPING_SELECT], false)
);


export const getShippingAddressChanged = (state, item) => (
    state.getIn([ITEM_DISPLAY_STORE_NAME, item.get('id'), SHIPPING_ADDRESS_CHANGED], false)
);


export const getShippingMethodChanged = (state, item) => (
    state.getIn([ITEM_DISPLAY_STORE_NAME, item.get('id'), SHIPPING_METHOD_CHANGED], false)
);


const isEditingBy = editingBy => state => (
    state.get(ITEM_DISPLAY_STORE_NAME).some(editingBy)
);


export const isEditingDetails = isEditingBy(itemDisplay => itemDisplay.get(EDIT_DETAILS_KEY));


export const isEditingAddress = isEditingBy(itemDisplay => itemDisplay.get(EDIT_ADDRESS_KEY));


export const isEditingQuantity = isEditingBy(itemDisplay => itemDisplay.get(EDIT_QUANTITY_KEY));


export const itemsAreEditing = state => (
    isEditingDetails(state) || isEditingAddress(state)
);






export const groupByShippingMethodAndAddress = createSelector(
    getPlasticItems,
    items => items.groupBy(item => `${item.get('addressID')}::${item.get('shippingMethod')}`)
);


export const createShippingGroup = (items, shipMethod, address, promoShippingMethods) => {
    const matchingShippingPromoMethod = getMatchingShippingPromoMethod(promoShippingMethods, shipMethod);
    let shippingCost = 0;

    if (shipMethod) {
        shippingCost = matchingShippingPromoMethod
            ? matchingShippingPromoMethod.get('updatedCost') : shipMethod.get('cost');
    }

    return fromJS({
        items,
        address,
        shippingMethod: shipMethod,
        sumFaceValue: parseFloat(sumFaceDenoms(items).toFixed(2)),
        shippingCost
    });
};


export const shippingGroupsForAddressAndMethod = (state, items) => {
    const promoShippingMethods = getShippingPromoData(state);
    const quantity = getCardsCount(items);
    const methodID = items.get(0).get(giftCardFieldNames.SHIPPING_METHOD_FIELD);
    const addressID = items.get(0).get(giftCardFieldNames.ADDRESS_ID);
    const currency = items.get(0).get(giftCardFieldNames.CURRENCY_FIELD);
    const address = getAddress(state, addressID);

    const hasAccessory = items.some(itemHasAccessory);
    const promoAccessories = getAccessoriesPromoData(state);
    const updatedItems = items.map(item => getItemWithUpdatedPromoAccessoryCosts(item)(promoAccessories));

    const profile = getShippingMethodProfileId(addressID, quantity, currency, hasAccessory);
    const qtyMethod = getShippingMethod(state, profile, methodID);

    
    if (qtyMethod) {
        return fromJS([createShippingGroup(updatedItems, qtyMethod, address, promoShippingMethods)]);
    }

    
    
    
    const singleProfile = getShippingMethodProfileId(addressID, 1, currency, hasAccessory);
    const singleMethod = getShippingMethod(state, singleProfile, methodID);
    return fromJS([createShippingGroup(updatedItems, singleMethod, address, promoShippingMethods)]);
};


export const shippingGroups = state => (
    groupByShippingMethodAndAddress(state).map(
        items => shippingGroupsForAddressAndMethod(state, items)
    ).reduce(
        (allGroups, groups) => allGroups.concat(groups), IList()
    )
);


export const getShippingGroups = createSelector(
    shippingGroups,
    groups => groups
);


export const getShippingTotal = createSelector(
    getShippingGroups,
    groups => groups.reduce((acc, group) => (acc + parseFloat(group.get('shippingCost'))), 0)
);






export const getDigitalTotal = createSelector(
    getPaidDigitalItems,
    items => sumFaceDenoms(items)
);


export const getPlasticTotal = createSelector(
    getPaidPlasticItems,
    items => sumFaceDenoms(items)
);


export const getUYOPQuantity = createSelector(
    getPaidPlasticItems,
    (items) => {
        const uyopCards = items.filter(item => item.has('uyoImageURL'));
        return uyopCards.reduce((totalQuantity, card) => totalQuantity + card.get('quantity', 0), 0);
    }
);


export const getSubTotal = createSelector(
    [getDigitalTotal, getPlasticTotal],
    
    
    (digital, plastic) => new Big(digital).add(plastic)
);


export const getUYOPFeesTotal = createSelector(
    getUYOPFeeAmount,
    getUYOPQuantity,
    (uyopFeeAmount, uyopQuantity) => new Big(uyopFeeAmount).times(uyopQuantity)
);


export const getHasUYOPFees = createSelector(
    getUYOPFeesTotal,
    uyopFeesTotal => uyopFeesTotal.gt(0)
);


export const getPlasticActivationFeesTotal = createSelector(
    getPlasticActivationFeeAmount,
    getPaidPlasticCardsCount,
    (plasticActivationFeeAmount, plasticQuantity) => new Big(plasticActivationFeeAmount).times(plasticQuantity)
);


export const getHasPlasticActivationFees = createSelector(
    getPlasticActivationFeesTotal,
    plasticActivationFeeAmount => plasticActivationFeeAmount.gt(0)
);


export const getPlasticActivationFeesCardsCount = createSelector(
    getHasPlasticActivationFees,
    getPaidPlasticCardsCount,
    (hasPlasticActivationFees, plasticQuantity) => (hasPlasticActivationFees ? plasticQuantity : 0)
);


export const getDigitalActivationFeesTotal = createSelector(
    getDigitalActivationFeeAmount,
    getPaidDigitalCardsCount,
    (digitalActivationFeeAmount, digitalQuantity) => new Big(digitalActivationFeeAmount).times(digitalQuantity)
);


export const getHasDigitalActivationFees = createSelector(
    getDigitalActivationFeesTotal,
    digitalActivationFeeAmount => digitalActivationFeeAmount.gt(0)
);


export const getDigitalActivationFeesCardsCount = createSelector(
    getHasDigitalActivationFees,
    getPaidDigitalCardsCount,
    (hasDigitalActivationFees, digitalQuantity) => (hasDigitalActivationFees ? digitalQuantity : 0)
);


export const getActivationFeesTotal = createSelector(
    getDigitalActivationFeesTotal,
    getPlasticActivationFeesTotal,
    (plasticActivationFeeAmount, digitalActivationFeeAmount) => new Big(plasticActivationFeeAmount).add(digitalActivationFeeAmount)
);


export const getHasActivationFees = createSelector(
    getHasDigitalActivationFees,
    getHasPlasticActivationFees,
    (hasPlasticActivationFee, hasDigitalActivationFee) => (hasPlasticActivationFee || hasDigitalActivationFee)
);


export const getActivationFeesCardsCount = createSelector(
    getPlasticActivationFeesCardsCount,
    getDigitalActivationFeesCardsCount,
    (digitalActivationFeesCardsCount, plasticActivationFeesCardsCount) => (digitalActivationFeesCardsCount + plasticActivationFeesCardsCount)
);


export const getFeesTotal = createSelector(
    getActivationFeesTotal,
    getUYOPFeesTotal,
    (uyopFeesTotal, activationFeesTotal) => new Big(uyopFeesTotal).add(activationFeesTotal)
);


export const getTotalCost = createSelector(
    [getSubTotal, getShippingTotal, getFeesTotal, getItemAccessoriesGrandTotalCost],
    (sub, ship, feesTotal, accessoriesTotal) => new Big(sub).add(ship).add(feesTotal).add(accessoriesTotal)
);


export const getTotalCostIncludingNewItems = createSelector(
    [getSubTotal, getShippingTotal, getFeesTotal, newItemDenomination],
    (sub, ship, feesTotal, newItemTotal) => new Big(sub).add(ship).add(feesTotal).add(newItemTotal)
);


export const getGrandTotalCost = createSelector(
    [getTotalCost, getDiscountRewardAmount],
    (totalCost, discount) => new Big(totalCost).add(discount)
);


export const getIsCartMaxed = createSelector(
    [getSubTotal, getMaxTransactionAmount, getDiscountRewardAmount],
    (subTotal, maxTrans, discount) => (subTotal.add(discount).gte(maxTrans))
);


export const getCurrencyCodes = createSelector(
    getPaidItems,
    items => items.map(item => getItemCurrency(item)).toSet()
);

export const getItemCurrencyList = createSelector(
    newItemIsPlastic,
    getPlasticCurrencyList,
    getElectronicCurrencyList,
    (isPlastic, plasticCurrencyList, electronicCurrencyList) => (
        isPlastic ? plasticCurrencyList : electronicCurrencyList)
);


export const showCurrencyPicker = createSelector(
    getMultipleCurrenciesSupported,
    getItemCurrencyList,
    (supportsMultipleCurrencies, itemCurrencyList) => (
        supportsMultipleCurrencies && itemCurrencyList.size > 1)
);


export const canAddDigitalItem = createSelector(
    hasDigitalFaceplates,
    getElectronicCurrencyList,
    getCurrencyCodes,
    (hasDigital, electronicCurrencyList, currencyCodes) => {
        if (currencyCodes.size > 0) {
            return hasDigital && electronicCurrencyList.includes(currencyCodes.first());
        }
        return hasDigital && electronicCurrencyList.size > 0;
    }
);


export const canAddPlasticItem = createSelector(
    hasPlasticFaceplates,
    getPlasticCurrencyList,
    getCurrencyCodes,
    (hasPlastic, plasticCurrencyList, currencyCodes) => {
        if (currencyCodes.size > 0) {
            return hasPlastic && plasticCurrencyList.includes(currencyCodes.first());
        }
        return hasPlastic && plasticCurrencyList.size > 0;
    }
);


export const canAddPlasticUYOItem = createSelector(
    getSupportedUYO,
    getPlasticCurrencyList,
    getCurrencyCodes,
    (supportedUYO, plasticCurrencyList, currencyCodes) => {
        if (currencyCodes.size > 0) {
            return supportedUYO.get('supportsUYOPlasticPhoto') && plasticCurrencyList.includes(currencyCodes.first());
        }
        return supportedUYO.get('supportsUYOPlasticPhoto') && plasticCurrencyList.size > 0;
    }
);


export const canAddDigitalUYOItem = createSelector(
    getSupportedUYO,
    getElectronicCurrencyList,
    getCurrencyCodes,
    (supportedUYO, electronicCurrencyList, currencyCodes) => {
        if (currencyCodes.size > 0) {
            return supportedUYO.get('supportsUYOEgcPhoto') && electronicCurrencyList.includes(currencyCodes.first());
        }
        return supportedUYO.get('supportsUYOEgcPhoto') && electronicCurrencyList.size > 0;
    }
);


export const canUploadUYOPhoto = createSelector(
    canAddPlasticUYOItem,
    canAddDigitalUYOItem,
    (canAddPlastic, canAddDigital) => (
        canAddPlastic || canAddDigital
    )
);
